import { Plus } from "lucide-react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { documentStore, selectionStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import { getActivePage, getSceneNodeChildren, resolveActivePageId, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import { isComponentInstanceDropParent } from "@/document/operations/nodeLocking";
import type { FlatLayerRowDropIndicator } from "./FlatLayerRow";
import { ComponentsPanel } from "./ComponentsPanel";
import {
  buildFlatLayerRows,
  flattenedVisibleLayerNodeIds,
  layerDisplayName,
  type LayerDropTarget,
} from "./layerList";
import { PageContextMenu } from "./PageContextMenu";
import { VirtualLayerList } from "./VirtualLayerList";
import { CURVA_SCENE_NODE_DRAG_TYPE, serializeSceneNodeDrag, setActiveSceneNodeDrag } from "./shellTransfer";

export type { LayerDropTarget } from "./layerList";
export { flattenedVisibleLayerNodeIds, layerDisplayName };

interface LeftPanelProps {
  onSwitchPage: (pageId: string) => void;
  onAddPage: () => void;
  onRenamePage: (pageId: string, name: string) => void;
  onDeletePage: (pageId: string) => void;
  onDuplicatePage: (pageId: string) => void;
  onSelectNodes: (nodeIds: string[], primaryNodeId?: string) => void;
  onRenameNode: (nodeId: string, name: string) => void;
  onVisibilityChange: (nodeId: string, visible: boolean) => void;
  onLockedChange: (nodeId: string, locked: boolean) => void;
  onMoveNodes: (nodeIds: string[], target: LayerDropTarget) => void;
  onContextMenu: (nodeId: string, point: { x: number; y: number }) => void;
  onComponentPlacementStart: (componentId: string) => void;
  onLocateComponent: (componentId: string) => void;
  allowScripts?: boolean;
  onEditScript?: (nodeId: string) => void;
}

type SidebarTab = "layers" | "components";

export interface SidebarSelectionModifiers {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}

export function LeftPanel({
  onSwitchPage,
  onAddPage,
  onRenamePage,
  onDeletePage,
  onDuplicatePage,
  onSelectNodes,
  onRenameNode,
  onVisibilityChange,
  onLockedChange,
  onMoveNodes,
  onContextMenu,
  onComponentPlacementStart,
  onLocateComponent,
  allowScripts = false,
  onEditScript,
}: LeftPanelProps) {
  const document = useStoreSubscribe(documentStore.document);
  const selection = useStoreSubscribe(selectionStore.selection);
  const activePageId = resolveActivePageId(document, selection.activePageId);
  const selectedNodeIds = selection.selectedNodeIds;
  const primarySelectedNodeId = selection.primarySelectedNodeId;
  const [activeTab, setActiveTab] = useState<SidebarTab>("layers");
  const [collapsedNodeIds, setCollapsedNodeIds] = useState<Set<string>>(() => new Set());
  const [draggedNodeIds, setDraggedNodeIds] = useState<string[]>([]);
  const [dropIndicator, setDropIndicator] = useState<FlatLayerRowDropIndicator | undefined>(undefined);
  const [editingNodeId, setEditingNodeId] = useState<string | undefined>(undefined);
  const [editingPageId, setEditingPageId] = useState<string | undefined>(undefined);
  const [pageContextMenu, setPageContextMenu] = useState<{ pageId: string; x: number; y: number } | undefined>(undefined);
  const [scrollToNodeId, setScrollToNodeId] = useState<string | undefined>(undefined);
  const sidebarSelectedNodeIdRef = useRef<string | undefined>(undefined);
  const selectionAnchorNodeIdRef = useRef<string | undefined>(primarySelectedNodeId);
  const activePage = getActivePage(document, activePageId);

  const flatRows = useMemo(
    () => (activePage ? buildFlatLayerRows(activePage.children, activePage.id, "page", collapsedNodeIds) : []),
    [activePage, collapsedNodeIds, document.revision],
  );

  const visibleLayerNodeIds = useMemo(
    () => (activePage ? flattenedVisibleLayerNodeIds(activePage.children, collapsedNodeIds) : []),
    [activePage, collapsedNodeIds, document.revision],
  );

  const isValidDropTarget = useCallback((target: LayerDropTarget): boolean => {
    return !isComponentInstanceDropParent(document, target.parentId);
  }, [document]);

  useLayoutEffect(() => {
    if (!primarySelectedNodeId) {
      sidebarSelectedNodeIdRef.current = undefined;
      selectionAnchorNodeIdRef.current = undefined;
      setScrollToNodeId(undefined);
      return;
    }

    if (sidebarSelectedNodeIdRef.current === primarySelectedNodeId) {
      sidebarSelectedNodeIdRef.current = undefined;
      setScrollToNodeId(undefined);
      return;
    }

    selectionAnchorNodeIdRef.current = primarySelectedNodeId;
    setScrollToNodeId(primarySelectedNodeId);
    const ancestorIds = ancestorNodeIds(document, primarySelectedNodeId);
    if (ancestorIds.length === 0) {
      return;
    }

    setCollapsedNodeIds(current => {
      let changed = false;
      const next = new Set(current);
      for (const ancestorId of ancestorIds) {
        if (next.delete(ancestorId)) {
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [document, primarySelectedNodeId]);

  const toggleCollapsed = useCallback((nodeId: string): void => {
    setCollapsedNodeIds(current => {
      const next = new Set(current);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const clearDragState = useCallback((): void => {
    setActiveSceneNodeDrag(undefined);
    setDraggedNodeIds([]);
    setDropIndicator(undefined);
  }, []);

  const selectNodeFromSidebar = useCallback((nodeId: string, modifiers: SidebarSelectionModifiers): void => {
    const next = sidebarSelectionForClick({
      clickedNodeId: nodeId,
      selectedNodeIds,
      visibleNodeIds: visibleLayerNodeIds,
      anchorNodeId: selectionAnchorNodeIdRef.current ?? primarySelectedNodeId,
      modifiers,
    });
    selectionAnchorNodeIdRef.current = next.anchorNodeId;
    sidebarSelectedNodeIdRef.current = nodeId;
    onSelectNodes(next.selectedNodeIds, next.primaryNodeId);
  }, [onSelectNodes, primarySelectedNodeId, selectedNodeIds, visibleLayerNodeIds]);

  const handleDragStartNode = useCallback((nodeId: string, event: React.DragEvent<HTMLDivElement>) => {
    const nextDraggedNodeIds = selectedNodeIds.includes(nodeId) ? selectedNodeIds : [nodeId];
    setDraggedNodeIds(nextDraggedNodeIds);
    setActiveSceneNodeDrag({ version: 1, sourceNodeId: nodeId, nodeIds: nextDraggedNodeIds });
    event.dataTransfer.effectAllowed = "copyMove";
    event.dataTransfer.setData("text/plain", nextDraggedNodeIds.join(","));
    event.dataTransfer.setData(CURVA_SCENE_NODE_DRAG_TYPE, serializeSceneNodeDrag({
      sourceNodeId: nodeId,
      nodeIds: nextDraggedNodeIds,
    }));
  }, [selectedNodeIds]);

  const handleEmptyPageDrop = useCallback(() => {
    if (!activePage || draggedNodeIds.length === 0) {
      return;
    }
    onMoveNodes(draggedNodeIds, { parentId: activePage.id, parentType: "page", index: 0 });
    clearDragState();
  }, [activePage, clearDragState, draggedNodeIds, onMoveNodes]);

  const registerRowElement = useCallback((_nodeId: string, _element: HTMLDivElement | null): void => {
    // Virtual list scrolls via flat row offsets; row refs are not needed.
  }, []);

  const clearScrollToNode = useCallback(() => setScrollToNodeId(undefined), []);

  return (
    <aside className="flex h-full min-h-0 w-64 shrink-0 flex-col border-r border-white/10 bg-[#191d22] text-[12px] text-slate-300">
      <div className="shrink-0 border-b border-white/10">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="font-medium text-slate-100">Pages</span>
          <button
            type="button"
            aria-label="Add page"
            title="Add page"
            onClick={onAddPage}
            className="grid size-6 place-items-center rounded-sm text-slate-400 transition-colors hover:bg-white/10 hover:text-slate-100"
          >
            <Plus className="size-3.5" aria-hidden="true" />
          </button>
        </div>
        <div className="max-h-[200px] space-y-0.5 overflow-y-auto overscroll-contain px-2 pb-2">
          {document.pages.map(page => {
            const active = page.id === activePage?.id;
            const editing = editingPageId === page.id;
            return (
              <div
                key={page.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!editing) {
                    onSwitchPage(page.id);
                  }
                }}
                onDoubleClick={event => {
                  event.preventDefault();
                  setPageContextMenu(undefined);
                  setEditingPageId(page.id);
                }}
                onContextMenu={event => {
                  event.preventDefault();
                  setEditingPageId(undefined);
                  setPageContextMenu({ pageId: page.id, x: event.clientX, y: event.clientY });
                }}
                onKeyDown={event => {
                  if (editing) {
                    return;
                  }
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSwitchPage(page.id);
                  }
                }}
                className={`flex w-full items-center rounded px-2 py-1.5 text-left transition-colors outline-none ${
                  active
                    ? "bg-sky-500/15 text-sky-50 ring-1 ring-inset ring-sky-400/40"
                    : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200"
                }`}
              >
                {editing ? (
                  <RenameInput
                    name={page.name}
                    onCommit={name => {
                      setEditingPageId(undefined);
                      onRenamePage(page.id, name);
                    }}
                    onCancel={() => setEditingPageId(undefined)}
                  />
                ) : (
                  <span className="truncate">{page.name}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {pageContextMenu ? (
        <PageContextMenu
          x={pageContextMenu.x}
          y={pageContextMenu.y}
          canDelete={document.pages.length > 1}
          onRename={() => {
            setEditingPageId(pageContextMenu.pageId);
            setPageContextMenu(undefined);
          }}
          onDuplicate={() => {
            onDuplicatePage(pageContextMenu.pageId);
            setPageContextMenu(undefined);
          }}
          onDelete={() => {
            onDeletePage(pageContextMenu.pageId);
            setPageContextMenu(undefined);
          }}
          onClose={() => setPageContextMenu(undefined)}
        />
      ) : null}
      <div className="shrink-0 border-b border-white/10 px-3 py-2">
        <div className="flex gap-1">
          <SidebarTabButton active={activeTab === "layers"} onClick={() => setActiveTab("layers")}>
            Layers
          </SidebarTabButton>
          <SidebarTabButton active={activeTab === "components"} onClick={() => setActiveTab("components")}>
            Components
          </SidebarTabButton>
        </div>
      </div>
      {activeTab === "layers" ? (
        activePage ? (
          <VirtualLayerList
            rows={flatRows}
            selectedNodeIds={selectedNodeIds}
            primarySelectedNodeId={primarySelectedNodeId}
            collapsedNodeIds={collapsedNodeIds}
            draggedNodeIds={draggedNodeIds}
            dropIndicator={dropIndicator}
            editingNodeId={editingNodeId}
            scrollToNodeId={scrollToNodeId}
            onSelectNode={selectNodeFromSidebar}
            onRenameNode={onRenameNode}
            onVisibilityChange={onVisibilityChange}
            onLockedChange={onLockedChange}
            onMoveNodes={onMoveNodes}
            onContextMenu={onContextMenu}
            onToggleCollapsed={toggleCollapsed}
            onDragStartNode={handleDragStartNode}
            onDragEndNode={clearDragState}
            onDragOverNode={setDropIndicator}
            onDropNode={target => {
              if (draggedNodeIds.length > 0 && isValidDropTarget(target)) {
                onMoveNodes(draggedNodeIds, target);
              }
              clearDragState();
            }}
            isValidDropTarget={isValidDropTarget}
            onEditNode={setEditingNodeId}
            onRowElement={registerRowElement}
            onScrollToNodeComplete={clearScrollToNode}
            allowScripts={allowScripts}
            onEditScript={onEditScript}
            emptyPageDrop={activePage.children.length === 0 ? {
              parentId: activePage.id,
              onDrop: handleEmptyPageDrop,
            } : undefined}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-auto p-2 px-2 py-3 text-[11px] text-slate-500">No pages</div>
        )
      ) : (
        <ComponentsPanel
          document={document}
          onComponentPlacementStart={onComponentPlacementStart}
          onLocateComponent={onLocateComponent}
        />
      )}
    </aside>
  );
}

function SidebarTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-2.5 py-1 text-[12px] font-medium transition-colors ${
        active
          ? "bg-white/10 text-slate-100"
          : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200"
      }`}
    >
      {children}
    </button>
  );
}

export function sidebarSelectionForClick({
  clickedNodeId,
  selectedNodeIds,
  visibleNodeIds,
  anchorNodeId,
  modifiers,
}: {
  clickedNodeId: string;
  selectedNodeIds: string[];
  visibleNodeIds: string[];
  anchorNodeId?: string;
  modifiers: SidebarSelectionModifiers;
}): { selectedNodeIds: string[]; primaryNodeId?: string; anchorNodeId: string } {
  if (modifiers.shiftKey) {
    const clickedIndex = visibleNodeIds.indexOf(clickedNodeId);
    const anchorIndex = anchorNodeId ? visibleNodeIds.indexOf(anchorNodeId) : -1;
    if (clickedIndex >= 0 && anchorIndex >= 0) {
      const start = Math.min(anchorIndex, clickedIndex);
      const end = Math.max(anchorIndex, clickedIndex);
      return {
        selectedNodeIds: visibleNodeIds.slice(start, end + 1),
        primaryNodeId: clickedNodeId,
        anchorNodeId: anchorNodeId ?? clickedNodeId,
      };
    }
  }

  if (modifiers.metaKey || modifiers.ctrlKey) {
    const selected = selectedNodeIds.includes(clickedNodeId)
      ? selectedNodeIds.filter(id => id !== clickedNodeId)
      : [...selectedNodeIds, clickedNodeId];
    return {
      selectedNodeIds: selected,
      primaryNodeId: selected.includes(clickedNodeId) ? clickedNodeId : selected.at(-1),
      anchorNodeId: clickedNodeId,
    };
  }

  return {
    selectedNodeIds: [clickedNodeId],
    primaryNodeId: clickedNodeId,
    anchorNodeId: clickedNodeId,
  };
}

function RenameInput({ name, onCommit, onCancel }: { name: string; onCommit: (name: string) => void; onCancel: () => void }) {
  const [draft, setDraft] = useState(name);
  const trimmedDraft = draft.trim();
  return (
    <input
      className="min-w-0 flex-1 rounded-sm border border-sky-400/70 bg-[#0f1318] px-1 py-0 text-[12px] text-slate-50 outline-none"
      value={draft}
      autoFocus
      onFocus={event => event.currentTarget.select()}
      onChange={event => setDraft(event.target.value)}
      onClick={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
      onBlur={() => onCommit(trimmedDraft || name)}
      onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          onCommit(trimmedDraft || name);
        } else if (event.key === "Escape") {
          event.preventDefault();
          onCancel();
        }
      }}
    />
  );
}

function ancestorNodeIds(document: CurvaDocument, nodeId: string): string[] {
  for (const page of document.pages) {
    const ancestors = ancestorNodeIdsInNodes(page.children, nodeId, []);
    if (ancestors) {
      return ancestors;
    }
  }
  return [];
}

function ancestorNodeIdsInNodes(nodes: SceneNode[], nodeId: string, ancestors: string[]): string[] | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return ancestors;
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const found = ancestorNodeIdsInNodes(children, nodeId, [...ancestors, node.id]);
    if (found) {
      return found;
    }
  }
  return undefined;
}
